import {
  BlotterTable,
  BlotterTableFilters,
  RestBlotterTableMaxRecordsReachedWarning,
  type BlotterTableFiltersProps,
  type RestBlotterPaginationLimit,
  type UseBlotterTable,
} from '../../BlotterTable';
import { Box } from '../../Core';
import { PanelContent } from '../../Panel';
import type { EntityAdminClass } from '../EntityAdminClass';
import type { EntityAdminRecord } from '../types';
import { useEntityAdminHeaderLayout } from './EntityAdminHeaderLayoutContext';

export const EntityAdminBlotter = <TRecord extends EntityAdminRecord>({
  blotterTable,
  blotterTableFilter,
  paginationLimit,
  suppressBlotterTableFilters,
  blotterTableHidden,
  blotterTableOverlay,
  blotterTableFooter,
}: {
  /** The blotter table props. */
  blotterTable: UseBlotterTable<EntityAdminClass<TRecord>>;

  /** The blotter table filters. */
  blotterTableFilter: BlotterTableFiltersProps;

  /** REST-specific pagination limit. */
  paginationLimit?: RestBlotterPaginationLimit;

  /** Whether to suppress rendering filters. */
  suppressBlotterTableFilters?: boolean;

  /** When true, hides the blotter table while keeping filters visible. */
  blotterTableHidden?: boolean;

  /** Optional overlay rendered over the blotter table area. */
  blotterTableOverlay?: React.ReactNode;

  /** Optional footer rendered below the blotter table. */
  blotterTableFooter?: React.ReactNode;
}) => {
  const { isPanelLayout } = useEntityAdminHeaderLayout();
  const rowEndSpacing = isPanelLayout ? 'spacing12' : 'spacing24';

  return (
    <PanelContent pb={0} px={0} flex={1} minHeight={0} overflow="hidden">
      {suppressBlotterTableFilters ? null : <BlotterTableFilters {...blotterTableFilter} />}
      {paginationLimit && <RestBlotterTableMaxRecordsReachedWarning {...paginationLimit} />}
      <Box flex={1} minHeight={0} position={blotterTableOverlay != null ? 'relative' : undefined}>
        <BlotterTable rowEndSpacing={rowEndSpacing} {...blotterTable} hidden={blotterTableHidden} />
        {blotterTableOverlay}
      </Box>
      {blotterTableFooter}
    </PanelContent>
  );
};
